/**
 * PlayAd-style asset viewer with versions: aspect tabs, "V2 ▾" version picker (every render attempt
 * incl. superseded ones, with QA verdict + the fix instruction that produced it), ‹ › stepping,
 * download, "Select" (drag a region to target an edit) and a bottom "+ Describe edits" bar that
 * requests the next version.
 *
 * Data: the `assets` from creatives.get (all versions, ordered aspect → attempts).
 * Tool: creatives.regenerate_asset {brandId, creativeId, aspect, instruction, mode:"edit"|"regenerate"}
 *   → {jobId, creativeId, aspect, mode}. The new version appears once the job renders + QA's it.
 */
import { ArrowUp, ChevronDown, ChevronLeft, ChevronRight, Download, Plus, SquareDashedMousePointer, SquarePen, X } from "lucide-react";
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useToolMutation } from "@/api/hooks";
import type { Creative, CreativeAsset } from "@/api/types";
import { cn } from "@/lib/cn";
import { relative } from "@/lib/format";
import { Pill } from "../ui/Badge";
import { Segmented } from "../ui/Form";
import { Dropdown, MenuItem, Tooltip } from "../ui/Overlay";
import { CreativeMedia } from "./CreativeBits";
import { safeMediaHref } from "@/lib/safe-url";
import { Link } from "react-router";
import { generationHref, generationReceipt } from "./GenerationProgress";
import { brandPath } from "@/state/brand";
import { editorPath } from "@/pages/editor/paths";

interface Region {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const ASPECT_ORDER = ["1:1", "9:16", "4:5", "16:9"];
const pct = (n: number) => Math.round(Math.max(0, Math.min(1, n)) * 100);

function versionsFor(assets: CreativeAsset[], aspect: string): CreativeAsset[] {
  const t = (a: CreativeAsset) => {
    const c = (a as unknown as { createdAt?: string }).createdAt;
    return c ? Date.parse(c) : 0;
  };
  return assets.filter((a) => a.aspect === aspect).sort((a, b) => (a.attempts ?? 0) - (b.attempts ?? 0) || t(a) - t(b));
}

function verdictOf(a: CreativeAsset): string | undefined {
  return a.qaStatus ?? a.qaReport?.verdict;
}

export function AssetVersionsViewer({
  brandId,
  creative,
  blockedReason,
  onEditRequested,
  className,
}: {
  brandId: string;
  creative: Creative;
  /** Why edits are disabled (in an ad account, still generating…); null = editable. */
  blockedReason: string | null;
  onEditRequested?: () => void;
  className?: string;
}) {
  const assets = useMemo(() => {
    const all=creative.assets ?? [];
    if(creative.kind !== "video") return all;
    const videos=all.filter(a=>a.mediaType === "video");
    // Synced Meta JPEGs are posters, not playable versions of the video.
    const poster=creative.thumbnailUrl ?? all.find(a=>a.mediaType === "image" && a.status !== "superseded")?.url;
    return videos.length ? videos.map(a=>({...a,thumbnailUrl:a.thumbnailUrl ?? poster})) : all.map(a=>({...a, mediaType:"video" as const, url:null, thumbnailUrl:a.thumbnailUrl ?? a.url}));
  }, [creative.assets, creative.kind, creative.thumbnailUrl]);
  const aspects = useMemo(() => Array.from(new Set(assets.map((a) => a.aspect))).sort((a, b) => ASPECT_ORDER.indexOf(a) - ASPECT_ORDER.indexOf(b)), [assets]);
  const [aspect, setAspect] = useState<string | null>(null);
  const cur = aspect && aspects.includes(aspect) ? aspect : (aspects[0] ?? null);
  const versions = useMemo(() => (cur ? versionsFor(assets, cur) : []), [assets, cur]);
  const latestIdx = Math.max(
    0,
    versions.findLastIndex((v) => v.status !== "superseded"),
  );
  const [picked, setPicked] = useState<Record<string, number>>({});
  const latestPlayableIdx = versions.findLastIndex(v => !!safeMediaHref(v.url));
  const defaultIdx = versions[latestIdx]?.url ? latestIdx : latestPlayableIdx >= 0 ? latestPlayableIdx : latestIdx;
  const idx = cur && picked[cur] !== undefined && picked[cur]! < versions.length ? picked[cur]! : defaultIdx;
  const v = versions[idx];
  const [selecting, setSelecting] = useState(false);
  const [region, setRegion] = useState<Region | null>(null);
  const [text, setText] = useState("");
  const [receipt, setReceipt] = useState<ReturnType<typeof generationReceipt>>(null);
  const [requested, setRequested] = useState<Record<string, number>>({});
  const boxRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const isVideo = creative.kind === "video";

  const edit = useToolMutation("creatives.regenerate_asset", {
    success: isVideo ? "New take requested — it goes through QA, then lands here as the next version" : "Edit requested — the next version renders, goes through QA, then lands here",
    onSuccess: (res) => {
      setReceipt(generationReceipt(res));
      setText("");
      setRegion(null);
      setSelecting(false);
      if (cur) setRequested((r) => ({ ...r, [cur]: versions.length + 1 }));
      onEditRequested?.();
    },
  });

  if (!cur || !v) return null;

  const setIdx = (i: number) => setPicked((p) => ({ ...p, [cur]: Math.max(0, Math.min(versions.length - 1, i)) }));
  const pos = (e: ReactPointerEvent) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const onDown = (e: ReactPointerEvent) => {
    if (!selecting) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = pos(e);
    setRegion({ x0: drag.current.x, y0: drag.current.y, x1: drag.current.x, y1: drag.current.y });
  };
  const onMove = (e: ReactPointerEvent) => {
    if (!selecting || !drag.current) return;
    const p = pos(e);
    setRegion({ x0: drag.current.x, y0: drag.current.y, x1: p.x, y1: p.y });
  };
  const onUp = () => {
    drag.current = null;
    setRegion((r) => (r && Math.abs(r.x1 - r.x0) > 0.02 && Math.abs(r.y1 - r.y0) > 0.02 ? r : null));
  };
  const box = region ? { left: Math.min(region.x0, region.x1), top: Math.min(region.y0, region.y1), w: Math.abs(region.x1 - region.x0), h: Math.abs(region.y1 - region.y0) } : null;
  const regionText = box
    ? `Only change the selected region — ${pct(box.left)}–${pct(box.left + box.w)}% from the left, ${pct(box.top)}–${pct(box.top + box.h)}% from the top of the frame; keep everything else identical. `
    : "";
  const instruction = `${regionText}${text.trim()}`;
  const send = () => {
    if (blockedReason || edit.isPending || text.trim().length < 3) return;
    edit.mutate({ brandId, creativeId: creative.id, aspect: cur, instruction: instruction.slice(0, 2000), mode: isVideo ? "regenerate" : "edit" });
  };
  const editInstr = (a: CreativeAsset) => (a as unknown as { editInstruction?: string | null }).editInstruction ?? null;
  const waitingFor = requested[cur];
  const tall = cur === "9:16" || cur === "4:5";

  return (
    <section className={cn("flex flex-col overflow-hidden rounded-card border border-line bg-[#111113]", className)} aria-label="Asset versions">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-line px-3 py-2">
        <Dropdown
          trigger={
            <button type="button" className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-elevated px-2.5 text-[13px] font-medium hover:bg-elevated-2" aria-label="Select a version">
              V{idx + 1}
              <ChevronDown className="size-3.5 text-muted" />
            </button>
          }
          className="w-[300px]"
        >
          {(close) =>
            versions.map((a, i) => {
              const verdict = verdictOf(a);
              return (
                <MenuItem
                  key={a.id}
                  selected={i === idx}
                  onClick={() => {
                    setIdx(i);
                    close();
                  }}
                  icon={<span className="text-[11px] font-semibold text-fg">V{i + 1}</span>}
                  description={editInstr(a) ? `Fix: ${editInstr(a)}` : a.status === "superseded" ? "Superseded attempt" : i === latestIdx ? "Current version" : undefined}
                  right={
                    verdict === "fail" ? (
                      <Pill tone="danger" size="xs">
                        QA fail
                      </Pill>
                    ) : verdict === "pass" ? (
                      <Pill tone="success" size="xs">
                        QA pass
                      </Pill>
                    ) : null
                  }
                >
                  {i === latestIdx ? "Latest" : `Attempt ${a.attempts ?? i + 1}`}
                </MenuItem>
              );
            })
          }
        </Dropdown>
        <button
          type="button"
          aria-label="Previous version"
          disabled={idx <= 0}
          onClick={() => setIdx(idx - 1)}
          className="grid size-8 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg disabled:opacity-30"
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          aria-label="Next version"
          disabled={idx >= versions.length - 1}
          onClick={() => setIdx(idx + 1)}
          className="grid size-8 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg disabled:opacity-30"
        >
          <ChevronRight className="size-4" />
        </button>
        {safeMediaHref(v.url) && (
          <a
            href={safeMediaHref(v.url)}
            download
            target="_blank"
            rel="noreferrer"
            aria-label="Download this version"
            className="grid size-8 place-items-center rounded-lg text-link hover:bg-hover hover:text-lavender"
          >
            <Download className="size-4" strokeWidth={1.75} />
          </a>
        )}
        {!isVideo && (
          <Tooltip content={blockedReason ?? "Drag a box over what should change"}>
            <button
              type="button"
              disabled={!!blockedReason || idx !== latestIdx}
              aria-pressed={selecting}
              onClick={() => {
                setSelecting((s) => !s);
                if (selecting) setRegion(null);
              }}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] transition-colors disabled:opacity-35",
                selecting ? "bg-primary-soft text-lavender" : "text-fg/90 hover:bg-hover",
              )}
            >
              <SquareDashedMousePointer className="size-4" strokeWidth={1.75} />
              Select
            </button>
          </Tooltip>
        )}
        {!isVideo && (
          <Link to={editorPath(brandId, creative.id, cur)} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] text-fg/90 hover:bg-hover">
            <SquarePen className="size-4" strokeWidth={1.75} />
            Open in editor
          </Link>
        )}
        <div className="ml-auto">
          {aspects.length > 1 && (
            <Segmented
              size="sm"
              value={cur}
              onChange={(a) => {
                setAspect(a);
                setRegion(null);
              }}
              options={aspects.map((a) => ({ value: a, label: a }))}
            />
          )}
        </div>
      </div>

      {/* media */}
      <div className="relative flex items-center justify-center bg-[#0d0d0f] p-4">
        <div
          ref={boxRef}
          className={cn(
            "relative overflow-hidden rounded-lg",
            tall ? "h-[420px] max-h-[56vh] aspect-[9/16]" : "aspect-square w-full max-w-[420px]",
            selecting && "cursor-crosshair touch-none select-none",
          )}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <CreativeMedia
            url={v.url}
            poster={v.thumbnailUrl}
            mediaType={v.mediaType}
            controls={v.mediaType === "video"}
            fit="contain"
            className={cn("size-full", selecting && "pointer-events-none")}
            alt={`${creative.name} ${cur} V${idx + 1}`}
          />
          {box && (
            <span
              className="pointer-events-none absolute rounded-[3px] border-2 border-lavender bg-primary/15 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
              style={{ left: `${box.left * 100}%`, top: `${box.top * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }}
            />
          )}
          {selecting && !box && (
            <span className="pointer-events-none absolute inset-x-0 top-2 mx-auto w-fit rounded-full bg-black/70 px-2.5 py-1 text-[11.5px] text-white">Drag to select the area to edit</span>
          )}
        </div>
        {v.status === "superseded" && <span className="absolute left-4 top-4 rounded-full bg-black/70 px-2 py-0.5 text-[11px] text-white/90">Earlier version · V{latestIdx + 1} is current</span>}
      </div>

      {idx !== latestIdx && !versions[latestIdx]?.url && <p className="px-4 py-2 text-xs text-muted">Showing the last retained artifact. The latest attempt has no media yet.</p>}

      {/* version meta */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line px-3 py-2 text-[11.5px] text-muted">
        <span className="font-medium text-fg/90">
          {cur} · V{idx + 1}
        </span>
        {verdictOf(v) === "pass" && (
          <Pill tone="success" size="xs">
            QA pass
          </Pill>
        )}
        {verdictOf(v) === "fail" && (
          <Pill tone="danger" size="xs">
            QA fail{v.qaReport?.issues?.length ? ` · ${v.qaReport.issues.length}` : ""}
          </Pill>
        )}
        {v.model && <span>· {v.model}</span>}
        {editInstr(v) && <span className="min-w-0 truncate">· Fix: {editInstr(v)}</span>}
        {(v as unknown as { createdAt?: string }).createdAt && <span className="ml-auto">{relative((v as unknown as { createdAt?: string }).createdAt)}</span>}
        {waitingFor && waitingFor > versions.length && (
          <Pill tone="primary" size="xs">
            V{waitingFor} rendering…
          </Pill>
        )}
      </div>

      {(v.status === "failed" || v.status === "qa_failed" || verdictOf(v) === "fail") && <p role="alert" className="border-t border-danger/30 bg-danger-soft/30 px-4 py-3 text-[12.5px] text-danger">Failed — awaiting your instructions. This render is retained for review; no automatic regeneration. Describe what to change, then explicitly request one new attempt.</p>}
      {verdictOf(v) === "pending" && <p className="px-4 py-2 text-xs text-muted">QA pending — preview only, not approved for publishing.</p>}

      {v.error && <p className="px-4 py-2 text-xs text-danger">{v.error}</p>}
      {!!v.qaReport?.issues?.length && <ul aria-label="QA issues" className="space-y-1 px-4 py-2 text-xs text-danger">{v.qaReport.issues.map((issue, i) => <li key={i}>{issue.kind}: {issue.detail}</li>)}</ul>}
      {receipt && <div className="flex gap-4 px-4 py-3 text-sm text-link" role="status"><Link to={generationHref(brandId, receipt)}>View flow</Link><Link to={brandPath(brandId, `creations?flow=${encodeURIComponent(receipt.flowId)}&run=${encodeURIComponent(receipt.runId)}`)}>View creations</Link></div>}
      {/* describe edits */}
      <div className="border-t border-line p-2.5">
        {box && (
          <div className="mb-2 flex items-center gap-1.5 px-1 text-[11.5px] text-lavender">
            <SquareDashedMousePointer className="size-3.5" /> Region selected
            <button type="button" className="ml-1 grid size-4 place-items-center rounded-full hover:bg-primary/30" aria-label="Clear region" onClick={() => setRegion(null)}>
              <X className="size-3" />
            </button>
          </div>
        )}
        <div className={cn("flex items-center gap-2 rounded-full border border-line-strong bg-surface-2 py-1 pl-2 pr-1 focus-within:border-primary", blockedReason && "opacity-60")}>
          <Tooltip content={isVideo ? "Video edits re-render the whole take" : "Select a region first for a targeted edit"}>
            <button
              type="button"
              aria-label="Select a region"
              disabled={!!blockedReason || isVideo}
              onClick={() => setSelecting(true)}
              className="grid size-7 shrink-0 place-items-center rounded-full text-muted hover:bg-hover hover:text-fg disabled:opacity-40"
            >
              <Plus className="size-4" />
            </button>
          </Tooltip>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={!!blockedReason || edit.isPending}
            placeholder={blockedReason ?? (idx !== latestIdx ? `Describe edits — applies to the latest version (V${latestIdx + 1})` : "Describe edits")}
            aria-label="Describe edits"
            className="h-8 min-w-0 flex-1 bg-transparent text-[13.5px] outline-none placeholder:text-subtle disabled:cursor-not-allowed"
          />
          <button
            type="button"
            aria-label="Request edit"
            onClick={send}
            disabled={!!blockedReason || text.trim().length < 3 || edit.isPending}
            className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-white transition-colors hover:bg-primary-hover disabled:bg-elevated disabled:text-subtle"
          >
            <ArrowUp className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
